import {
  CheckCircle,
  MagnifyingGlass,
  PaperPlaneTilt,
  WarningCircle,
} from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { IOrderOutcome, OrderStatus } from 'types/Orders.types';
import {
  canRetryOrder,
  getOrderColor,
  isOrderToCheckWithSupplier,
} from 'utils/orderUtils';
import {
  Container,
  IconWrapper,
  Reason,
  Text,
  TextWrapper,
} from './OrderStatusNote.styled';

interface IOrderStatusNoteProps {
  order?: IOrderOutcome & {
    status?: string;
    connector_id?: number | null;
    internal_number?: string | null;
  };
  // On the supplier's page: a failed order that may already be at the supplier
  // is theirs to check, and to set Successful if it is there.
  isSupplier?: boolean;
}

// What Logyx core knows about an order that did not simply go through (BP-1219):
// it is being sent, its outcome is being checked, why it failed and whether it
// can be sent again, or that it had been created before.
const OrderStatusNote = ({ order, isSupplier }: IOrderStatusNoteProps) => {
  const { t } = useTranslation();
  if (!order) return null;

  const reason = order.outcome_message;
  let text: string | null = null;
  let icon = WarningCircle;
  let showsReason = !!reason;

  switch (order.status) {
    case OrderStatus.SENDING:
      text = t(
        'This order is being sent to the supplier. This page updates by itself.'
      );
      icon = PaperPlaneTilt;
      showsReason = false;
      break;
    case OrderStatus.UNKNOWN:
      text = t(
        "The supplier's system did not confirm this order. It may already be there, so it cannot be sent again. It is being checked: do not order these products again."
      );
      icon = MagnifyingGlass;
      break;
    case OrderStatus.FAILED:
      if (canRetryOrder(order)) {
        text = t(
          'This order could not be placed. You can send it again with Retry.'
        );
      } else if (isOrderToCheckWithSupplier(order)) {
        // Only the supplier can tell. One that is not in their system is
        // released by Logyx support (a platform admin's "not in NAV"), and can
        // then be sent again.
        text = isSupplier
          ? t(
              'This order could not be placed, but it may already be in your system. Check it there: if it is, set the status to Successful. If it is not, contact Logyx support.'
            )
          : t(
              'This order could not be placed, and it may already be at the supplier, so it cannot be sent again from here. Ask the supplier to check it.'
            );
      }
      // Otherwise a manual order (no connector) the supplier set FAILED: nothing
      // Logyx knows to add, and no note.
      break;
    case OrderStatus.SUCCESSFUL:
      if (order.replayed && order.internal_number) {
        text = t(
          'Already created as {{number}}. This order was not sent to the supplier again.',
          { number: order.internal_number }
        );
        icon = CheckCircle;
        showsReason = false;
      }
      break;
  }

  if (!text) return null;
  const color = getOrderColor(order);

  return (
    <Container
      $color={color}
      data-testid="order-status-note"
      data-status={order.status}
    >
      <IconWrapper>
        <Icon svg={icon} color={color} size={24} weight="regular" />
      </IconWrapper>
      <TextWrapper>
        <Text>{text}</Text>
        {showsReason && (
          <Reason data-testid="order-status-note-reason">
            {t('Reason: {{reason}}', { reason })}
          </Reason>
        )}
      </TextWrapper>
    </Container>
  );
};

export default OrderStatusNote;
